import { create } from 'zustand';
import { apiService } from '@/services/api';
import type {
  BillingStatus,
  SubscriptionInfo,
  PlanDto,
  CreditPackDto,
  PaymentDto,
  CheckoutInitiateData,
  CheckoutStatusData,
} from '@/types';

interface BillingState {
  // Status
  activeSubscription: SubscriptionInfo | null;
  creditsBalance: number;
  creditsUsedThisMonth: number;

  // Plans & Products
  availablePlans: PlanDto[];
  creditPacks: CreditPackDto[];

  // Payments
  payments: PaymentDto[];

  // Checkout
  checkoutData: CheckoutInitiateData | null;
  checkoutStatus: CheckoutStatusData | null;

  // UI state
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchBillingStatus: () => Promise<void>;
  fetchPlans: () => Promise<void>;
  fetchCreditPacks: () => Promise<void>;
  fetchPayments: () => Promise<void>;
  addToCart: (planId: number, productId: number) => Promise<boolean>;
  checkout: (paymentType: string) => Promise<boolean>;
  initiateCheckout: () => Promise<CheckoutInitiateData | null>;
  pollCheckoutStatus: (paymentId: number) => Promise<CheckoutStatusData | null>;
  clearCheckout: () => void;
}

export const useBillingStore = create<BillingState>((set) => ({
  activeSubscription: null,
  creditsBalance: 0,
  creditsUsedThisMonth: 0,
  availablePlans: [],
  creditPacks: [],
  payments: [],
  checkoutData: null,
  checkoutStatus: null,
  isLoading: false,
  error: null,

  fetchBillingStatus: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.getBillingStatus();
      if (response.value) {
        const status: BillingStatus = response.value;
        set({
          activeSubscription: status.activeSubscription,
          creditsBalance: status.creditsBalance,
          creditsUsedThisMonth: status.creditsUsedThisMonth,
        });
      }
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load billing status' });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchPlans: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.getPlans();
      if (response.value) {
        set({ availablePlans: response.value });
      }
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load plans' });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchCreditPacks: async () => {
    try {
      const response = await apiService.getCreditPacks();
      if (response.value) {
        set({ creditPacks: response.value });
      }
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load credit packs' });
    }
  },

  fetchPayments: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.getPaymentHistory();
      if (response.value) {
        set({ payments: response.value });
      }
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load payments' });
    } finally {
      set({ isLoading: false });
    }
  },

  addToCart: async (planId: number, productId: number) => {
    set({ isLoading: true, error: null });
    try {
      await apiService.addToCart(planId, productId);
      return true;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to add to cart' });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  checkout: async (paymentType: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiService.checkout(paymentType);
      return true;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Checkout failed' });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  initiateCheckout: async () => {
    set({ isLoading: true, error: null, checkoutData: null });
    try {
      const response = await apiService.initiateCheckout();
      if (response.value) {
        set({ checkoutData: response.value });
        return response.value;
      }
      set({ error: 'Failed to initiate checkout' });
      return null;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to initiate checkout' });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },

  pollCheckoutStatus: async (paymentId: number) => {
    try {
      const response = await apiService.getCheckoutStatus(paymentId);
      if (response.value) {
        set({ checkoutStatus: response.value });
        return response.value;
      }
      return null;
    } catch {
      return null;
    }
  },

  clearCheckout: () => {
    set({ checkoutData: null, checkoutStatus: null, error: null });
  },
}));
